import { Trans, useTranslation } from '@jq4race/i18n';
import type { PhotoAngle } from '@jq4race/shared';
import { Button } from '@jq4race/ui';
import { Camera, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface CameraCaptureProps {
  /** Vehicle angle — used by the mandatory 4-angle capture flow (JQ4-22). */
  angle?: PhotoAngle;
  /**
   * Optional display label — overrides the angle-derived viewfinder text.
   * Used by angle-less capture flows (e.g. incident photos) that have no
   * `PhotoAngle` to fall back to (ticket-incident-photos).
   */
  label?: string;
  onCapture: (blob: Blob) => void;
  onCancel: () => void;
}

type CameraError = 'NotAllowedError' | 'NotFoundError' | 'OverconstrainedError' | 'unknown';

const MAX_BLOB_BYTES = 10 * 1024 * 1024; // 10 MB
const MAX_WIDTH = 1920;
const MAX_HEIGHT = 1080;

/**
 * Opens the rear camera, streams video, and captures a single JPEG frame.
 * Handles permission errors (SC-5) and enforces the 10 MB size limit (FR-9).
 * Stops the stream on unmount and after capture (FR-12).
 */
export function CameraCapture({ angle, label, onCapture, onCancel }: CameraCaptureProps) {
  const { t } = useTranslation('checkin');
  const displayLabel = label ?? angle ?? t('cameraCapture.defaultLabel');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraError, setCameraError] = useState<CameraError | null>(null);
  const [sizeError, setSizeError] = useState(false);
  const [ready, setReady] = useState(false);

  const stopStream = useCallback(() => {
    for (const t of streamRef.current?.getTracks() ?? []) {
      t.stop();
    }
    streamRef.current = null;
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function startCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });

        if (cancelled) {
          for (const t of stream.getTracks()) {
            t.stop();
          }
          return;
        }

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setReady(true);
      } catch (err: unknown) {
        if (cancelled) return;
        const name = err instanceof Error ? err.name : 'unknown';
        if (name === 'NotAllowedError') setCameraError('NotAllowedError');
        else if (name === 'NotFoundError') setCameraError('NotFoundError');
        else if (name === 'OverconstrainedError') setCameraError('OverconstrainedError');
        else setCameraError('unknown');
      }
    }

    void startCamera();

    return () => {
      cancelled = true;
      stopStream();
    };
  }, [stopStream]);

  function handleCapture() {
    if (!videoRef.current) return;
    setSizeError(false);

    const video = videoRef.current;
    const vw = video.videoWidth || video.clientWidth || 640;
    const vh = video.videoHeight || video.clientHeight || 480;

    // Downscale to max 1920×1080 preserving aspect ratio
    let dw = vw;
    let dh = vh;
    if (dw > MAX_WIDTH || dh > MAX_HEIGHT) {
      const ratio = Math.min(MAX_WIDTH / dw, MAX_HEIGHT / dh);
      dw = Math.round(dw * ratio);
      dh = Math.round(dh * ratio);
    }

    const canvas = document.createElement('canvas');
    canvas.width = dw;
    canvas.height = dh;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.drawImage(video, 0, 0, dw, dh);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        if (blob.size > MAX_BLOB_BYTES) {
          setSizeError(true);
          return;
        }
        stopStream();
        onCapture(blob);
      },
      'image/jpeg',
      0.85,
    );
  }

  // ── Error states ───────────────────────────────────────────────────────────

  if (cameraError === 'NotAllowedError') {
    return (
      <div className="flex flex-col gap-3 rounded-sm border border-destructive/60 bg-destructive/10 p-4">
        <p className="jq-body-sm font-semibold text-destructive">
          {t('cameraCapture.permissionDeniedTitle')}
        </p>
        <p className="jq-body-sm text-muted-foreground">
          {t('cameraCapture.permissionDeniedBody')}
        </p>
        <ul className="list-disc pl-4 text-[13px] text-muted-foreground space-y-1">
          <li>
            <Trans
              i18nKey="checkin:cameraCapture.iosInstructions"
              components={{ strong: <strong />, em: <em /> }}
            />
          </li>
          <li>
            <Trans
              i18nKey="checkin:cameraCapture.androidInstructions"
              components={{ strong: <strong />, em: <em /> }}
            />
          </li>
        </ul>
        <Button
          type="button"
          variant="ghost"
          className="h-12 w-full text-[14px]"
          onClick={onCancel}
        >
          <RotateCcw className="size-4" />
          {t('cameraCapture.retry')}
        </Button>
      </div>
    );
  }

  if (cameraError === 'NotFoundError' || cameraError === 'OverconstrainedError') {
    return (
      <div className="flex flex-col gap-3 rounded-sm border border-border bg-card p-4">
        <p className="jq-body-sm text-muted-foreground">{t('cameraCapture.noCamera')}</p>
        <Button
          type="button"
          variant="ghost"
          className="h-12 w-full text-[14px]"
          onClick={onCancel}
        >
          <RotateCcw className="size-4" />
          {t('cameraCapture.cancel')}
        </Button>
      </div>
    );
  }

  if (cameraError === 'unknown') {
    return (
      <div className="flex flex-col gap-3 rounded-sm border border-destructive/60 bg-destructive/10 p-4">
        <p className="jq-body-sm text-destructive">{t('cameraCapture.unknownError')}</p>
        <Button
          type="button"
          variant="ghost"
          className="h-12 w-full text-[14px]"
          onClick={onCancel}
        >
          {t('cameraCapture.cancel')}
        </Button>
      </div>
    );
  }

  // ── Live viewfinder ────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-3">
      <div className="relative overflow-hidden rounded-sm bg-black aspect-video">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          title={t('cameraCapture.viewfinderTitle', { label: displayLabel })}
          className="w-full h-full object-cover"
        />
        {!ready && (
          <div
            role="status"
            className="absolute inset-0 flex items-center justify-center bg-black/60"
          >
            <span className="jq-caption text-white">{t('cameraCapture.loading')}</span>
          </div>
        )}
      </div>

      {sizeError && (
        <p role="alert" className="jq-body-sm text-destructive">
          {t('cameraCapture.imageTooLarge')}
        </p>
      )}

      <div className="flex gap-3">
        <Button
          type="button"
          variant="ghost"
          className="h-12 flex-1 text-[14px]"
          onClick={onCancel}
          aria-label={t('cameraCapture.cancelAria')}
        >
          {t('cameraCapture.cancel')}
        </Button>
        <Button
          type="button"
          size="touch"
          className="h-14 flex-1 text-[15px]"
          onClick={handleCapture}
          disabled={!ready}
          aria-label={t('cameraCapture.captureAria')}
        >
          <Camera className="size-5" />
          {t('cameraCapture.capture')}
        </Button>
      </div>
    </div>
  );
}
